import { EnumListType } from "gql/generated";
import { useState } from "react";
import { Flex, Box } from "styled-system/jsx";
import {
  AnimatedPage,
  Collapsible,
  filterOptions,
  type FilterScreenType,
} from "shared/components";
import { AnimatePresence } from "framer-motion";
import {
  AllFilters,
  FilterScreen,
  ListFilters,
  MINIMUM_RADIUS,
  useFilters,
} from "./filterScreens";
import { Button } from "@ttc3k/trekker";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { isLongLatValid } from "shared/utils";

type FilterSidebarProps = {
  type: EnumListType;
  appliedFilters: ListFilters;
  search: string;
  onUpdateAppliedFilters: (filter: Partial<ListFilters>) => void;
  onUpdateSearch: (value: string) => void;
};

export const FilterSidebar = ({
  type,
  appliedFilters,
  search,
  onUpdateAppliedFilters,
  onUpdateSearch,
}: FilterSidebarProps) => {
  const [screen, setScreen] = useState<FilterScreenType>("ALL");

  const { t } = useTranslation("core", {
    keyPrefix: "FILTER",
  });

  const currentFilterOption = filterOptions.find((op) => op.key === screen);

  const {
    filters,
    onApplyFilters,
    onUpdateFilters,
    onResetFilters,
    onClearAllPerFilterScreen,
  } = useFilters(appliedFilters, onUpdateAppliedFilters);

  const getCurrentScreen = (currScreen: FilterScreenType) => {
    const handleScreenChange = (scrn: FilterScreenType) => {
      setScreen(scrn);
      if (scrn === "ALL") {
        // If going back to the "ALL" filters screen, reset the filters
        onResetFilters();
      }
    };
    switch (currScreen) {
      case "ALL":
        return (
          <AllFilters
            type={type}
            onScreenChange={handleScreenChange}
            appliedFilters={appliedFilters}
            search={search}
            onUpdateSearch={onUpdateSearch}
          />
        );
      default:
        return (
          <FilterScreen
            onScreenChange={handleScreenChange}
            currentScreenFilterOption={currentFilterOption}
            screen={currScreen}
            filters={filters}
            onUpdateFilters={onUpdateFilters}
          />
        );
    }
  };

  const handleApplyFilters = () => {
    onApplyFilters();
    setScreen("ALL");
  };

  const handleClear = () => {
    onClearAllPerFilterScreen(screen);
    setScreen("ALL");
  };

  const isEditingFilters = !_.isEqual(filters, appliedFilters);

  // Only validate location filter if we are on the location screen
  const isLocationFilterValid =
    screen === "LOCATION"
      ? !!filters.location.address &&
        isLongLatValid(filters.location.longitude, filters.location.latitude) &&
        filters.location.radius >= MINIMUM_RADIUS
      : true;

  const disableApply = !isLocationFilterValid || !isEditingFilters;

  return (
    <Collapsible open={true} key={screen}>
      <AnimatePresence mode={"wait"}>
        <AnimatedPage
          key={screen}
          initial={screen === "ALL" ? "exit" : "enter"}
        >
          <Flex
            flexDir={"column"}
            // height equals whichever is the smallest: either 780px or 100vh
            // minus the modal header
            height={"min(780px, calc(100vh - 96px))"}
          >
            {/* Scrollable content wrapper */}
            <Flex
              position={"relative"}
              flexDir={"column"}
              overflowY={"auto"}
              flex={1}
            >
              <Box py={"500"} px={"400"}>
                <Flex flexDir={"column"} gap={"300"}>
                  {getCurrentScreen(screen)}
                </Flex>
              </Box>
            </Flex>

            {/* Footer */}
            {/* Only display the footer on individual filter screens */}
            {screen !== "ALL" && (
              <Flex
                py={"500"}
                px={"400"}
                borderTopWidth={"1px"}
                borderTopColor={"border.light"}
                borderTopStyle={"solid"}
                boxShadow={"0px -2px 12px 0px rgba(0, 0, 0, 0.08)"}
                gap={"150"}
              >
                <Button
                  size={"lg"}
                  width={"full"}
                  mode={"light"}
                  onClick={handleClear}
                >
                  {t("CLEAR_ALL")}
                </Button>
                <Button
                  size={"lg"}
                  width={"full"}
                  onClick={handleApplyFilters}
                  disabled={disableApply}
                >
                  {t("APPLY")}
                </Button>
              </Flex>
            )}
          </Flex>
        </AnimatedPage>
      </AnimatePresence>
    </Collapsible>
  );
};
